import type { ClassDefinition } from '../../schemas/classDefinition';
import { rangerAdvanced2to5Moves } from '../moves/ranger/advanced-2-5';
import { rangerAdvanced6to10Moves } from '../moves/ranger/advanced-6-10';
import { rangerStartingAutoMoves, rangerStartingChoiceMoves } from '../moves/ranger/initial';

/**
 * Definizione della classe **Ranger** (manuale V1, rr. 5130-5441).
 *
 * Ottava classe trascritta. Pattern principali stress-testati:
 * - **`MoveSchema.replaces`** terzo uso strutturale: *Grampasso* sostituisce
 *   *Ramingo* (entrambe sulla mossa "viaggio pericoloso"). Pattern già
 *   consolidato dopo Mago/FI-21 e Monaco (2 casi).
 * - **3 catene di prerequisiti** L2-5 → L6-10 (Seguire Tracce → Perspicace;
 *   Preda Familiare → La Preda del Cacciatore; Bersaglio Facile → Tiro
 *   Perfetto).
 * - **FI-8 stub** (decisione 2026-05-10): *Compagno Animale* + *Vecchi
 *   Amici* + *Riserva di Caccia* + 4 mosse advanced legate al Compagno
 *   sono description-only. Refactor unificato Ranghi 1-3 nella PR FI-8
 *   post-12 classi.
 * - ***Ramingo*** descrive trigger archetipo Elfo nel testo (description-only,
 *   coerente con altri pattern già visti).
 * - **Mosse multiclasse**: *Abilità Secondarie* e *Addestramento Avanzato*
 *   scelgono da 5 classi (Barbaro/Druido/Guerriero/Ladro/Sciamano).
 *   Description-only (FI-17).
 *
 * **Punti notevoli**:
 * - Dado di danno **d8**, PF base 8, Carico base 11
 * - 5 archetipi razza-tematici
 * - 3 mosse iniziali automatiche + 2 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (40 mosse totali)
 * - 4 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - **Non casteggiante**: `spellcasting` resta `undefined`
 * - 4 categorie di Aspetto (Occhi/Capelli/Vesti/Corpo)
 */
export const rangerClassDefinition: ClassDefinition = {
  id: 'ranger',
  name: 'Ranger',

  intro:
    "Questa gente di città… hanno mai udito il richiamo del lupo che risuona tra le montagne? Hanno sentito i venti gelidi ululare nei deserti sconfinati dell'Est? Hanno mai inseguito la preda con arco e coltello, sentendo il battito del loro cuore fondersi con quello della natura? No, certo che no. Ed è per questo che ti cercano.\n\nGuida. Cacciatore. Spirito selvaggio. Segugio infallibile. Sei tutto questo e molto di più. Hai vagato solitario per il mondo fino a oggi, ma un richiamo più potente – chiamalo destino, se preferisci – ti ha condotto a questa compagnia. Coraggiosi, forse. Forti, a modo loro. Ma solo tu conosci i sentieri nascosti che si snodano tra le ombre degli alberi secolari e le insidie delle terre inesplorate. Il tuo naso non fiuta solo il vento, ma anche la paura. I tuoi occhi non vedono solo l'ombra, ma anche la preda che vi si cela.\n\nForse hai appreso la lingua degli animali, e magari uno di loro è divenuto il tuo inseparabile compagno d'avventura. O forse frequenti qualche bettola puzzolente, col volto celato da un cappuccio nero, in cerca di indizi che possano guidarti alla tua prossima preda. E se anche il suo aspetto sembra quello di un essere umano, di un elfo o di un nano, per te non fa differenza. Una preda è sempre una preda, che ruggisca o che parli con voce suadente. Puoi braccarla nella foresta di Alberovecchio, sui crinali del Monte Calvo oppure tra le strade tortuose di Radburg, nei vicoli bui e verso i covi segreti dove si annida.\n\nSenza di te, sarebbero perduti. Fatti strada tra il sangue e le ombre, Grampasso. Conduci il loro cammino. E porta a termine la caccia!",

  // Manuale V1, rr. 5141-5149
  suggestedNames: [
    // Umano
    'Zev',
    'Jonah',
    'Halek',
    'Brandon',
    'Emory',
    'Shrike',
    'Nora',
    'Diana',
    'Redegast',
    'Gordon',
    // Elfo
    'Throndir',
    'Elrosine',
    'Aranwe',
    'Celion',
    'Dambrath',
    'Lanethe',
    'Golriath',
    'Selwen',
    // Nano
    'Gudkur',
    'Hjalo',
    'Bud',
    'Farki',
    'Chripri',
    'Dranrin',
    'Drinfar',
    'Kurmi',
    'Niga',
    'Dranme',
    // Halfling
    'Othocho',
    'Nasolo',
    'Gardli',
    'Holva',
    'Boldca',
    'Egarva',
    'Bebald',
    'Odose',
    'Gopy',
    // Mezz'orco
    'Gri',
    'Ronmog',
    'Triggdahk',
    'Orz',
    'Abaag',
    'Baglug',
    'Tossra',
    'Ska',
    'Ubra',
    'Mol',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 5151-5157
  aspetti: [
    {
      label: 'Occhi',
      options: ['Selvaggi', 'Acuti', 'Ferini'],
    },
    {
      label: 'Capelli',
      options: ['Incappucciato', 'Capelli selvaggi', 'Calvo'],
    },
    {
      label: 'Vesti',
      options: ['Mantello', 'Vesti mimetiche', 'Abiti da viaggio'],
    },
    {
      label: 'Corpo',
      options: ['Esile', 'Selvaggio', 'Temprato dagli elementi'],
    },
  ],

  // Manuale V1, rr. 5159-5165
  damageDie: 'd8',
  baseHp: 8,
  baseLoad: 11,

  // Manuale V1, rr. 5167-5175
  alignmentPrecepts: {
    buono: 'Ti metti in pericolo per aiutare un compagno in difficoltà.',
    legale: 'Onori un patto o una promessa, anche se ciò ha un costo per te.',
    neutrale: 'Aiuti un compagno in difficoltà, ma evitando di correre rischi.',
    caotico: 'Infrangi leggi e regole per ottenere un vantaggio sulla tua preda.',
    malvagio:
      'Ti approfitti di un alleato per ottenere un vantaggio personale, ignorando le conseguenze.',
  },

  // Manuale V1, rr. 5190-5193. **maxBonds: 4** (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    'Ho fatto da guida a _____ in passato e mi deve un favore.',
    '_____ non apprezza le mie abilità: gli dimostrerò che sta facendo un grave errore!',
    '_____ è un vero cacciatore. Ho molto da imparare da lui.',
    '_____ non comprende la vita nelle terre selvagge, perciò gli farò da maestro.',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 5195-5201
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Quando ti *accampi* in un dungeon o in una città, non hai bisogno di consumare una *razione*.',
      grantedMoveId: 'ranger.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando *intraprendi un viaggio pericoloso* attraverso le terre selvagge, qualsiasi ruolo tu assuma, riesci automaticamente come se avessi ottenuto un 10+.',
      grantedMoveId: 'ranger.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        "Quando ti *accampi*, puoi, a tua scelta, creare 1 utilizzo di Attrezzatura da Avventuriero o di *munizioni* (del tipo che vuoi), o eseguire una piccola riparazione su un oggetto d'equipaggiamento.",
      grantedMoveId: 'ranger.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Quando stai saccheggiando in cerca di provviste, trovi sempre 1d4 utilizzi di Razioni da Dungeon, se ciò è anche solo minimamente plausibile.',
      grantedMoveId: 'ranger.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Quando torni in un luogo dove ti sei fatto notare per la tua forza, coraggio o spietatezza in una precedente sessione, la tua reputazione ti precede. Se vieni riconosciuto, sarai inevitabilmente al centro della scena: tutti sanno chi sei, e anche i tuoi nemici ti temono o ti rispettano per quello che hai fatto.',
      grantedMoveId: 'ranger.archetipo-mezzorco',
    },
  ],

  startingMoves: rangerStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 5222: "Inoltre, scegli una delle seguenti mosse".
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description:
        'Scegli una di queste due mosse. *Compagno Animale* (FI-8 stub: le sotto-scelte di specie/modificatore/capacità restano in description fino alla PR FI-8 unificata post-12 classi).',
      moveOptions: rangerStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: rangerAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: rangerAdvanced6to10Moves.map((m) => m.id),

  // Il Ranger non è casteggiante: la sua connessione con la natura è
  // tracking, sopravvivenza, addestramento animale — non *Lanciare
  // Incantesimi* / *Mana* / Tradizioni Sacre.
  // spellcasting resta undefined.

  // Manuale V1, rr. 5177-5188
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'attrezzatura-da-avventuriero',
        name: 'Attrezzatura da Avventuriero',
        weight: 2,
        uses: 5,
        tags: [],
      },
      {
        id: 'armatura-di-cuoio-rigido',
        name: 'Armatura di cuoio rigido',
        weight: 1,
        armor: 1,
        tags: ['indossato'],
      },
      {
        id: 'coltello',
        name: 'Coltello',
        weight: 1,
        tags: ['corta'],
      },
      {
        id: 'arco-da-caccia',
        name: 'Arco da caccia',
        weight: 1,
        tags: ['lontano', 'a due mani'],
      },
      {
        id: 'faretra-di-frecce',
        name: 'Faretra di frecce',
        weight: 1,
        uses: 3,
        tags: [],
        description: '3 munizioni.',
      },
    ],
    choices: [
      {
        id: 'arma-iniziale',
        prompt: "Scegli un'opzione per la tua arma",
        pickCount: 1,
        options: [
          {
            id: 'spada-corta',
            label: 'Spada corta',
            items: [
              {
                id: 'spada-corta',
                name: 'Spada corta',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
          {
            id: 'lancia',
            label: 'Lancia',
            items: [
              {
                id: 'lancia',
                name: 'Lancia',
                weight: 1,
                tags: ['lunga', 'da lancio', 'vicino'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'razioni-extra',
            label: 'Razioni da Dungeon',
            items: [
              {
                id: 'razioni-da-dungeon-extra',
                name: 'Razioni da Dungeon',
                weight: 1,
                uses: 5,
                tags: ['razione'],
              },
            ],
          },
          {
            id: 'faretra-extra',
            label: 'Faretra di frecce',
            items: [
              {
                id: 'faretra-di-frecce-extra',
                name: 'Faretra di frecce',
                weight: 1,
                uses: 3,
                tags: [],
                description: '3 munizioni.',
              },
            ],
          },
        ],
      },
    ],
  },
};
